import { useState } from 'react';
import { MoreHorizontal, Pencil, Pin, PinOff, Trash2 } from 'lucide-react';
import { IconButton, Menu, MenuDivider, MenuItem, useToast } from '@/components/ui';
import { isApiError, useUpdateConversation, type Conversation } from '@/lib/api';
import { DeleteConversationDialog } from './DeleteConversationDialog';
import { isPinned } from './pinned';
import { RenameConversationDialog } from './RenameConversationDialog';
import { isWhatsAppSurface } from './surfaces';

/** Por que "Editar nome do chat" fica travado numa conversa aberta pelo bot. */
export const WHATSAPP_RENAME_LOCKED = 'Conversas do WhatsApp não podem ser editadas';
/** Por que "Excluir" fica travado numa conversa aberta pelo bot. */
export const WHATSAPP_DELETE_LOCKED = 'Conversas do WhatsApp não podem ser excluídas';

export interface ConversationRowMenuProps {
  conversation: Conversation;
  /** Classe do "⋯": cada lista decide quando ele aparece (hover, foco, toque). */
  triggerClassName?: string;
  /** Depois de excluir; a barra lateral o usa para voltar o chat ao vazio. */
  onDeleted?: () => void;
}

type OpenDialog = 'rename' | 'delete' | null;

/**
 * O "⋯" de uma conversa, o mesmo na barra lateral e na tela das conversas:
 * "Editar nome do chat", "Fixar no topo" (ou "Desafixar") e "Excluir", este
 * separado e com confirmação. As conversas do WhatsApp pertencem ao bot: aqui
 * elas só se fixam, e as outras duas ações aparecem travadas dizendo por quê,
 * em vez de sumir (a API as recusaria com 409 `whatsapp_readonly`).
 *
 * Cada ação é dona da própria escrita: o fixar mora no item, a edição e a
 * exclusão nos diálogos. Itens e diálogos só montam abertos, então a linha de
 * uma lista longa não carrega estado de escrita nenhum.
 */
export function ConversationRowMenu({
  conversation,
  triggerClassName,
  onDeleted,
}: ConversationRowMenuProps): JSX.Element {
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const locked = isWhatsAppSurface(conversation.surface);
  const closeDialog = (): void => setDialog(null);

  return (
    <>
      <Menu
        label={`Ações de ${conversation.title}`}
        align="end"
        trigger={
          <IconButton
            label={`Mais ações de ${conversation.title}`}
            icon={<MoreHorizontal />}
            size="sm"
            className={triggerClassName}
          />
        }
      >
        <MenuItem
          leading={<Pencil />}
          disabled={locked}
          description={locked ? WHATSAPP_RENAME_LOCKED : undefined}
          onSelect={() => setDialog('rename')}
        >
          Editar nome do chat
        </MenuItem>
        <PinItem conversation={conversation} />
        <MenuDivider />
        <MenuItem
          danger
          leading={<Trash2 />}
          disabled={locked}
          description={locked ? WHATSAPP_DELETE_LOCKED : undefined}
          onSelect={() => setDialog('delete')}
        >
          Excluir
        </MenuItem>
      </Menu>

      {dialog === 'rename' && <RenameConversationDialog conversation={conversation} onClose={closeDialog} />}
      {dialog === 'delete' && (
        <DeleteConversationDialog conversation={conversation} onClose={closeDialog} onDeleted={onDeleted} />
      )}
    </>
  );
}

/**
 * "Fixar no topo" / "Desafixar", gravado direto: a lista relida põe a conversa
 * no lugar novo. O menu fecha no clique e o item desmonta com ele; a escrita e o
 * aviso seguem pela promessa, que não depende do item continuar na tela.
 */
function PinItem({ conversation }: { conversation: Conversation }): JSX.Element {
  const update = useUpdateConversation();
  const { toast } = useToast();
  const pinned = isPinned(conversation);

  const toggle = async (): Promise<void> => {
    try {
      await update.mutateAsync({ id: conversation.id, patch: { pinned: !pinned } });
      toast({ tone: 'success', title: pinned ? 'Conversa desafixada.' : 'Conversa fixada no topo.' });
    } catch (err) {
      toast({
        tone: 'error',
        title: pinned ? 'Não foi possível desafixar a conversa.' : 'Não foi possível fixar a conversa.',
        description: isApiError(err) ? err.message : undefined,
      });
    }
  };

  return (
    <MenuItem leading={pinned ? <PinOff /> : <Pin />} onSelect={() => void toggle()}>
      {pinned ? 'Desafixar' : 'Fixar no topo'}
    </MenuItem>
  );
}
